import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, CanActivate, CanActivateChild, Router, RouterStateSnapshot } from '@angular/router';
import { Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { CompanyFeatures } from '@app/models/company-features.model';
import { RoleName } from '@app/models/user-role.model';
import { UserAPIService } from '@app/shared/api/user.api.service';
import { Globals } from '../globals/globals';

/**
 * Closes a colleague's profile, and every page beneath it, once a company turns on
 * RESTRICT_PROFILE_ACCESS. RESTRICT_PROFILE_ACCESS_SUBORDINATES_ONLY narrows a manager to their own
 * reporting line, which is anyone below them at any depth and nobody above or beside them.
 *
 * The id comes off the URL rather than the route params because the child route
 * `goals/individual/:id` declares an id of its own, which shadows the profile's in
 * `canActivateChild`. The leading group is the mount: ProfileModule is lazy-loaded at four paths,
 * so the redirect keeps the viewer under the one they came in by.
 */
@Injectable()
export class ProfileAccessGuard implements CanActivate, CanActivateChild {

  private static readonly PROFILE_URL = /^(.*?)\/profile\/(\d+)/;

  constructor(
    public globals: Globals,
    private router: Router,
    private userAPIService: UserAPIService
  ) { }

  public canActivate(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): boolean | Observable<boolean> {
    return this.check(state);
  }

  public canActivateChild(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): boolean | Observable<boolean> {
    return this.check(state);
  }

  private check(state: RouterStateSnapshot): boolean | Observable<boolean> {
    const match = state.url.match(ProfileAccessGuard.PROFILE_URL);

    if (!match) { return true; }

    const mount = match[1];
    const profileId = Number(match[2]);

    if (profileId === this.globals.user?.id) { return true; }
    if (!this.globals.hasFeature(CompanyFeatures.RESTRICT_PROFILE_ACCESS)) { return true; }
    if (this.globals.hasMinimumAccessLevelOf(RoleName.ADMIN)) { return true; }
    if (!this.globals.hasMinimumAccessLevelOf(RoleName.MANAGER)) { return this.sendToRestricted(mount); }
    if (!this.globals.hasFeature(CompanyFeatures.RESTRICT_PROFILE_ACCESS_SUBORDINATES_ONLY)) { return true; }

    return this.allowSubordinatesOnly(mount, profileId);
  }

  /**
   * The reporting line is read per navigation rather than held: a manager gains and loses reports
   * under them, and a stale list would either open a profile they no longer manage or close one
   * they now do. It fails closed, since the alternative is opening a profile on an unanswered
   * question.
   *
   * The closure table carries every depth, so a report's own report is covered without walking the
   * tree here, and the backend already drops the manager's own row.
   */
  private allowSubordinatesOnly(mount: string, profileId: number): Observable<boolean> {
    return this.userAPIService.findAllDescendantsForUserId(this.globals.user.id).pipe(
      map(hierarchy => hierarchy.some(row => row.descendantId === profileId)),
      catchError(() => of(false)),
      map(isSubordinate => isSubordinate ? true : this.sendToRestricted(mount))
    );
  }

  private sendToRestricted(mount: string): false {
    this.router.navigate([`${mount}/profile/restricted`]).then();
    return false;
  }
}
